import { createContext, useContext, useState, useCallback } from 'react';

interface Success {
  id: string;
  message: string;
  type: 'success' | 'achievement' | 'info';
}

interface SuccessContextType {
  successes: Success[];
  addSuccess: (message: string, type?: 'success' | 'achievement' | 'info') => void;
  removeSuccess: (id: string) => void;
  clearSuccesses: () => void;
}

const SuccessContext = createContext<SuccessContextType | undefined>(undefined);

export function SuccessProvider({ children }: { children: React.ReactNode }) {
  const [successes, setSuccesses] = useState<Success[]>([]);

  const addSuccess = useCallback((message: string, type: 'success' | 'achievement' | 'info' = 'success') => {
    const id = Math.random().toString(36).substr(2, 9);
    setSuccesses(prev => [...prev, { id, message, type }]);

    // Automatisches Entfernen nach 5 Sekunden
    setTimeout(() => {
      removeSuccess(id);
    }, 5000);
  }, []);

  const removeSuccess = useCallback((id: string) => {
    setSuccesses(prev => prev.filter(success => success.id !== id));
  }, []);

  const clearSuccesses = useCallback(() => {
    setSuccesses([]);
  }, []);

  return (
    <SuccessContext.Provider value={{ successes, addSuccess, removeSuccess, clearSuccesses }}>
      {children}
    </SuccessContext.Provider>
  );
}

export function useSuccess() {
  const context = useContext(SuccessContext);
  if (context === undefined) {
    throw new Error('useSuccess muss innerhalb eines SuccessProviders verwendet werden');
  }
  return context;
} 